import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { buildApiUrl } from './api';
import { useSEO } from './useSEO';

const LEVELS = ['GCSE', 'A-Level'];
const BOARDS = ['AQA', 'Edexcel', 'OCR'];

const GENERATION_MODES = [
  { value: 'predicted_paper', label: 'Predicted Paper', description: 'A realistic, full-length practice paper mirroring official exam structure and difficulty.' },
  { value: 'by_topic', label: 'Questions by Topic', description: 'Targeted questions based on uploaded specification pages or topic material.' },
  { value: 'short_answer', label: 'Short Answer Questions', description: 'Quick-fire recall and application questions ideal for revision drills.' },
];

const Q_TYPES = [
  { value: 'short', label: 'Short answer' },
  { value: 'mcq', label: 'Multiple choice' },
  { value: 'extended', label: 'Extended response' },
  { value: 'parent', label: 'Multi-part (parent)' },
];

export default function GenerateQuestions() {
  const { user, token } = useAuth();

  useSEO({
    title: 'Generate Questions',
    description: 'Generate exam-style question sets using AI for GCSE and A-Level subjects.',
    canonicalPath: '/generate_questions',
  });

  // ─── Step tracking ───
  const [step, setStep] = useState(1); // 1=config, 2=generating, 3=preview, 4=pdf

  // ─── Config state ───
  const [level, setLevel] = useState('');
  const [board, setBoard] = useState('');
  const [subject, setSubject] = useState('');
  const [generationMode, setGenerationMode] = useState('predicted_paper');
  const [questionCount, setQuestionCount] = useState(10);
  const [selectedTypes, setSelectedTypes] = useState(['short', 'mcq', 'extended']);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [contextFiles, setContextFiles] = useState([]);

  // ─── Results state ───
  const [questions, setQuestions] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [msLoading, setMsLoading] = useState(false);

  // Dynamic subjects list
  const [subjectsList, setSubjectsList] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const fetchSubjects = useCallback(async (lvl) => {
    if (!lvl) return;
    setSubjectsLoading(true);
    try {
      const safeLvl = encodeURIComponent(lvl.toLowerCase());
      const res = await fetch(buildApiUrl(`/api/subjects/${safeLvl}`));
      if (res.ok) {
        const data = await res.json();
        setSubjectsList(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    setSubjectsLoading(false);
  }, []);

  const handleLevelChange = (lvl) => {
    setLevel(lvl);
    setSubject('');
    setSubjectsList([]);
    fetchSubjects(lvl);
  };

  const handleFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length) {
      setContextFiles(prev => [...prev, ...incoming]);
    }
  };

  const removeFile = (idx) => {
    setContextFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleType = (t) => {
    setSelectedTypes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
  };

  const canGenerate = level && board && subject && selectedTypes.length > 0;

  // ─── Generate questions ───
  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError('');
    setGenerating(true);
    setStep(2);
    setQuestions(null);
    setPdfUrl(null);

    try {
      const formData = new FormData();
      formData.append('level', level);
      formData.append('board', board);
      formData.append('subject', subject);
      formData.append('questionCount', String(parseInt(questionCount, 10) || 10));
      formData.append('questionTypes', selectedTypes.join(','));
      formData.append('generationMode', generationMode);
      if (additionalInstructions.trim()) {
        formData.append('additionalInstructions', additionalInstructions.trim());
      }
      for (const f of contextFiles) {
        formData.append('files', f);
      }

      const res = await fetch(buildApiUrl('/api/generate-questions'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server returned ${res.status}`);
      }

      const data = await res.json();
      setQuestions(data.questions);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Generation failed');
      setStep(1);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Download PDF ───
  const handleDownloadPdf = async () => {
    if (!questions) return;
    setPdfLoading(true);
    setError('');

    try {
      const res = await fetch(buildApiUrl('/api/generate-questions/pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ questions }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'PDF generation failed');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions-${board}-${subject}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err.message || 'PDF download failed');
    } finally {
      setPdfLoading(false);
    }
  };

  // ─── Download Mark Scheme PDF ───
  const handleDownloadMarkScheme = async () => {
    if (!questions) return;
    setMsLoading(true);
    setError('');

    try {
      const res = await fetch(buildApiUrl('/api/generate-questions/markscheme-pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ questions }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Mark scheme PDF generation failed');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `markscheme-${board}-${subject}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Mark scheme download failed');
    } finally {
      setMsLoading(false);
    }
  };

  const totalMarks = useMemo(() => {
    if (!questions?.questions) return 0;
    return questions.questions.reduce((sum, q) => {
      const qMarks = q.marks || 0;
      const partMarks = (q.parts || []).reduce((s, p) => s + (p.marks || 0), 0);
      return sum + qMarks + partMarks;
    }, 0);
  }, [questions]);

  // ─── Render ───
  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card">
          <p>Please <a href="/login">log in</a> to use the question generator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="qgen-page">
      <h1 className="qgen-title">Question Generator</h1>
      <p className="qgen-subtitle">
        Generate exam-style questions using AI, then download as a formatted PDF.
      </p>

      {/* ── Step 1: Configuration ── */}
      {step === 1 && (
        <div className="qgen-config">
          {/* Level / Board / Subject selectors */}
          <div className="qgen-section">
            <h3>1. Select exam details</h3>
            <div className="qgen-row">
              <div className="qgen-field">
                <label>Level</label>
                <div className="qgen-chips">
                  {LEVELS.map(l => (
                    <button
                      key={l}
                      className={`qgen-chip ${level === l ? 'active' : ''}`}
                      onClick={() => handleLevelChange(l)}
                    >{l}</button>
                  ))}
                </div>
              </div>
              <div className="qgen-field">
                <label>Exam Board</label>
                <div className="qgen-chips">
                  {BOARDS.map(b => (
                    <button
                      key={b}
                      className={`qgen-chip ${board === b ? 'active' : ''}`}
                      onClick={() => setBoard(b)}
                    >{b}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="qgen-field">
              <label>Subject</label>
              {subjectsLoading ? (
                <p className="muted">Loading subjects…</p>
              ) : subjectsList.length > 0 ? (
                <div className="qgen-chips qgen-chips-wrap">
                  {subjectsList.map(s => (
                    <button
                      key={s}
                      className={`qgen-chip ${subject === s ? 'active' : ''}`}
                      onClick={() => setSubject(s)}
                    >{s}</button>
                  ))}
                </div>
              ) : (
                <input
                  type="text"
                  placeholder={level ? 'Type a subject name' : 'Select a level first'}
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="qgen-input"
                />
              )}
            </div>
          </div>

          {/* Context upload */}
          <div className="qgen-section">
            <h3>2. Upload specification / context <span className="muted">(optional)</span></h3>
            <p className="muted">Upload PDFs or images of specification pages, topic guides, or example questions.</p>
            <div className="qgen-drop">
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange}
              />
            </div>
            {contextFiles.length > 0 && (
              <div className="qgen-file-list">
                {contextFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="qgen-file-chip">
                    <span>{f.name}</span>
                    <button onClick={() => removeFile(i)} className="qgen-file-remove">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Generation mode */}
          <div className="qgen-section">
            <h3>3. Generation mode</h3>
            <div className="qgen-mode-cards">
              {GENERATION_MODES.map(m => (
                <button
                  key={m.value}
                  className={`qgen-mode-card ${generationMode === m.value ? 'active' : ''}`}
                  onClick={() => setGenerationMode(m.value)}
                >
                  <strong>{m.label}</strong>
                  <span>{m.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* AI config */}
          <div className="qgen-section">
            <h3>4. Question settings</h3>
            <div className="qgen-row">
              <div className="qgen-field">
                <label>Number of questions</label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={1}
                  max={30}
                  value={questionCount}
                  onChange={e => setQuestionCount(e.target.value === '' ? '' : parseInt(e.target.value, 10) || '')}
                  onBlur={() => setQuestionCount(prev => { const n = parseInt(prev, 10); return (!n || n < 1) ? 1 : n > 30 ? 30 : n; })}
                  className="qgen-input qgen-input-sm"
                />
              </div>
              <div className="qgen-field">
                <label>Question types</label>
                <div className="qgen-chips">
                  {Q_TYPES.map(t => (
                    <button
                      key={t.value}
                      className={`qgen-chip ${selectedTypes.includes(t.value) ? 'active' : ''}`}
                      onClick={() => toggleType(t.value)}
                    >{t.label}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="qgen-field">
              <label>Additional instructions <span className="muted">(optional)</span></label>
              <textarea
                value={additionalInstructions}
                onChange={e => setAdditionalInstructions(e.target.value)}
                placeholder="e.g. Focus on photosynthesis and respiration. Include at least 2 diagram-based questions."
                className="qgen-textarea"
                rows={3}
              />
            </div>
          </div>

          {error && <div className="qgen-error">{error}</div>}

          <button
            className="qgen-generate-btn"
            onClick={handleGenerate}
            disabled={!canGenerate}
          >
            Generate Questions
          </button>
        </div>
      )}

      {/* ── Step 2: Generating ── */}
      {step === 2 && (
        <div className="qgen-loading">
          <div className="qgen-spinner" />
          <p>Generating {questionCount} questions for {level} {board} {subject}…</p>
          <p className="muted">This may take 15–30 seconds depending on the context size.</p>
        </div>
      )}

      {/* ── Step 3: Preview ── */}
      {step === 3 && questions && (
        <div className="qgen-preview">
          <div className="qgen-preview-header">
            <div>
              <h2>{questions.title || `${subject} Questions`}</h2>
              <p className="muted">{level} · {board} · {subject} · {questions.questions?.length || 0} questions · {totalMarks} marks</p>
            </div>
            <div className="qgen-preview-actions">
              <button className="qgen-btn secondary" onClick={() => setStep(1)}>Edit settings</button>
              <button className="qgen-btn secondary" onClick={handleGenerate} disabled={generating}>Regenerate</button>
              <button className="qgen-btn primary" onClick={handleDownloadPdf} disabled={pdfLoading}>
                {pdfLoading ? 'Creating PDF…' : 'Download PDF'}
              </button>
              <button className="qgen-btn ms" onClick={handleDownloadMarkScheme} disabled={msLoading}>
                {msLoading ? 'Creating…' : 'Download Mark Scheme'}
              </button>
            </div>
          </div>

          {error && <div className="qgen-error">{error}</div>}

          {pdfUrl && (
            <div className="qgen-pdf-viewer">
              <iframe src={pdfUrl} title="Generated PDF" style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 12 }} />
            </div>
          )}

          <div className="qgen-questions-list">
            {(questions.questions || []).map((q, qi) => (
              <div key={qi} className="qgen-q-card">
                <div className="qgen-q-head">
                  <span className="qgen-q-num">{q.number || qi + 1}</span>
                  <span className={`qgen-q-type ${q.type}`}>{q.type}</span>
                  {q.marks > 0 && <span className="qgen-q-marks">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>}
                </div>
                <p className="qgen-q-text" dangerouslySetInnerHTML={{ __html: q.text }} />

                {q.diagram_image && (
                  <div className="qgen-diagram-img-wrap">
                    <img src={q.diagram_image} alt={q.diagram_description || 'Diagram'} className="qgen-diagram-img" />
                  </div>
                )}
                {!q.diagram_image && q.diagram_description && (
                  <div className="qgen-diagram-note">[Diagram: {q.diagram_description}]</div>
                )}

                {q.type === 'mcq' && q.options && (
                  <div className="qgen-options">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="qgen-option">
                        <span className="qgen-option-letter">{'ABCD'[oi] || oi + 1}</span>
                        <span>{opt}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(q.parts || []).length > 0 && (
                  <div className="qgen-parts">
                    {q.parts.map((p, pi) => (
                      <div key={pi} className="qgen-part">
                        <span className="qgen-part-label">({p.label})</span>
                        <span className="qgen-part-text" dangerouslySetInnerHTML={{ __html: p.text }} />
                        {p.marks > 0 && <span className="qgen-q-marks">{p.marks}m</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
